import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
} from 'react';
import { asText, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';

import { IconSvg } from '../IconSvg';
import { Placeholder } from '@/components';
import { currencyFormat } from 'utils/utils';

import styles from './FreeDeliveryAlert.module.scss';

interface IFreeDeliveryAlert {
  minDeliveryAmount: number;
  subtotal: number;
  messageCompleted?: RichTextField;
  message?: RichTextField;
  classname: string;
  isLoading?: boolean;
  totalDelivery?: number;
}
const FreeDeliveryAlert: FC<IFreeDeliveryAlert> = ({
  minDeliveryAmount,
  subtotal,
  message,
  messageCompleted,
  classname,
  isLoading = false,
}) => {
  if (!minDeliveryAmount) return null;

  if (isLoading) {
    return (
      <div
        aria-label="Loading delivery progress information"
        className={classNames(styles.free_delivery_progress, classname)}
        role="status"
      >
        <div className={styles.free_delivery_progress__skeleton}>
          <Placeholder
            aria-hidden="true"
            className={styles.free_delivery_progress__skeleton_message}
          />
          <Placeholder
            aria-hidden="true"
            className={styles.free_delivery_progress__skeleton_bar}
          />
        </div>
      </div>
    );
  }

  const porcentProgress = (subtotal * 100) / minDeliveryAmount;

  const processCompleted = subtotal >= minDeliveryAmount;

  const freeDeliveryThreshold = minDeliveryAmount - subtotal;

  const amountFormated = `$${currencyFormat(freeDeliveryThreshold)}`;
  const totalAmountFormatted = `$${(subtotal / 100).toFixed(2)}`;
  const minDeliveryAmountFormatted = `$${currencyFormat(minDeliveryAmount)}`;
  const progressLabel = processCompleted
    ? `Free delivery unlocked! Your order total is ${totalAmountFormatted}, ` +
      `which meets the ${minDeliveryAmountFormatted} minimum for free delivery.`
    : `Delivery progress: ${totalAmountFormatted} of ${minDeliveryAmountFormatted} ` +
      `needed for free delivery. ${amountFormated} more needed.`;

  type PropsWithChildren = {
    children?: ReactNode;
    [key: string]: unknown;
  };

  const replaceAmount = (children: ReactNode, amount: string): ReactNode => {
    // 1. Case string
    if (typeof children === 'string') {
      return children.replace(/\{\{amount\}\}/g, amount);
    }

    // 2. Case array
    if (Array.isArray(children)) {
      return children.map(child => replaceAmount(child, amount));
    }

    // 3. React Case Element
    if (isValidElement(children)) {
      const element = children as ReactElement<PropsWithChildren>;
      const props = {
        ...element.props,
        children: element.props.children
          ? replaceAmount(element.props.children, amount)
          : undefined,
      };

      return cloneElement(element, props);
    }

    // 4. Others case (numbers, booleans, etc)
    return children;
  };

  return (
    <div
      aria-label={progressLabel}
      aria-live="polite"
      className={classNames(styles.free_delivery_progress, classname)}
      role="status"
    >
      {processCompleted && (
        <div
          aria-label={`Free delivery unlocked. Your order total is ${totalAmountFormatted}`}
          className={classNames(
            styles.free_delivery_progress__message,
            styles.free_delivery_progress__message_completed,
          )}
          role="group"
        >
          <IconSvg aria-hidden="true" name="alert-success" />
          {asText(messageCompleted) ? (
            <PrismicRichText field={messageCompleted} />
          ) : (
            <p>
              Congrats! You&apos;ve unlocked <strong>free delivery!</strong>
            </p>
          )}
        </div>
      )}
      {!processCompleted && (
        <div
          aria-label={`Current order total: ${totalAmountFormatted}`}
          className={styles.free_delivery_progress__message}
          role="group"
        >
          <p>
            Your order: <strong>{totalAmountFormatted}</strong>
          </p>
        </div>
      )}
      <div className={styles.free_delivery_progress}>
        <progress
          aria-label={`Delivery progress: ${Math.round(
            porcentProgress,
          )}% complete`}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(porcentProgress)}
          className={classNames(styles.free_delivery_progress__bar, {
            [styles.free_delivery_progress__complete]: processCompleted,
          })}
          max={100}
          value={porcentProgress}
        />
      </div>
      {!processCompleted && (
        <div
          aria-label={`Add ${amountFormated} more to qualify for free delivery`}
          className={styles.free_delivery_progress__message}
          role="group"
        >
          {asText(message) ? (
            <PrismicRichText
              components={{
                paragraph: ({ children }) => (
                  <p>{replaceAmount(children, amountFormated)}</p>
                ),
              }}
              field={message}
            />
          ) : (
            <p>
              Spend <strong>{amountFormated}</strong> more for{' '}
              <strong className={styles.main_label}>Free delivery</strong>.
            </p>
          )}
        </div>
      )}
      {processCompleted && (
        <div
          className={classNames(
            styles.free_delivery_progress__message,
            styles.free_delivery_progress__message_completed,
          )}
        >
          <IconSvg name="alert-success" />
          {asText(messageCompleted) ? (
            <PrismicRichText field={messageCompleted} />
          ) : (
            <p>
              You now qualify for our <strong>Free delivery</strong>.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface IDeliveryFee {
  totalDelivery?: number;
  isLoading?: boolean;
  classname?: string;
}

const DeliveryFee: FC<IDeliveryFee> = ({
  totalDelivery,
  isLoading,
  classname,
}) => {
  if (isLoading) {
    return (
      <div className={styles.total_delivery__skeleton}>
        <Placeholder className={styles.total_delivery__skeleton_message} />
        <Placeholder className={styles.total_delivery__skeleton_value} />
      </div>
    );
  }
  const totalDeliveryFormatted = totalDelivery
    ? `$${currencyFormat(totalDelivery)}`
    : 'Free';
  return (
    <div className={classNames(styles.total_delivery, classname)}>
      <p
        aria-label={`Delivery fee: ${totalDeliveryFormatted}`}
        className={styles.fee}
        role="group"
      >
        Delivery fee
      </p>
      <span className={styles.total_fee}>{totalDeliveryFormatted}</span>
    </div>
  );
};

export { FreeDeliveryAlert, DeliveryFee };
